
import { Component, Emit, Model, Prop, Vue, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

import type { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';
import { VideoAdsHelper } from '@/helpers/video-ads-helper';
import { TitleDetailsNode } from '@/interfaces/title-details-graphql';
import { ClipsEvent } from './types';

import DisneyPlusPromo from '@/components/DisneyPlusPromo.vue';
import { DisneyPromoSettings } from '@/helpers/disneyPlusPromo';

import type { WebLocale } from '@/enums/web-locale';
import { getBackdropClips } from '@/helpers/title-helper';

const ImpressionPixel = () =>
	import(/* webpackChunkName: "impression-pixel" */ '@/components/sponsored-recommendations/ImpressionPixel.vue');

const BackdropCarousel = () =>
	import(/* webpackChunkName: "backdrop-carousel" */ '@/components/details/backdrops/BackdropCarousel.vue');
const BackdropTrailer = () =>
	import(/* webpackChunkName: "backdrop-trailer" */ '@/components/details/backdrops/BackdropTrailer.vue');

const Language = namespace('language');
const Routing = namespace('routing');
const User = namespace('user');

@Component({
	name: 'TitleBackdrops',
	components: {
		BackdropCarousel,
		BackdropTrailer,
		DisneyPlusPromo,
		ImpressionPixel,
	},
})
export default class TitleBackdrops extends Vue {
	@Prop({ required: true }) title: TitleDetailsNode;
	@Prop({ default: () => [] }) additionalContexts: SnowplowContext[];
	@Prop({ required: true }) isAVODActive: boolean;
	@Prop({ default: false }) hasDisneyOffer: boolean;

	@Model(ClipsEvent.MODEL, { default: null }) readonly activeClipId!: string | null;

	@Language.State webLocale: WebLocale;
	@Language.Getter country: string;
	@Routing.State activeRoute: any;

	@User.Getter isLoggedIn: () => boolean;
	@User.Getter isPremium: boolean;

	get disneyPlusOffer() {
		if (DisneyPromoSettings.countriesAvailable.includes(this.country) === false) return null;
		const offer = this.title.offers.find(offer => offer.package.packageId === DisneyPromoSettings.packageId);
		if (!offer) return null;
		return offer;
	}

	get titleName() {
		return this.title.content.title;
	}

	get titleClips() {
		// START AVOD
		const PlexClip = this.isAVODActive
			? [
					{
						provider: 'PLEX',
						externalId: this.title.plexPlayerOffers[0].id,
						sourceUrl: this.title.plexPlayerOffers[0].standardWebURL,
					},
			  ]
			: null;
		// END AVOD
		return PlexClip || getBackdropClips(this.title);
	}

	get hasBackdropContent(): boolean {
		const hasCarouselImages = this.title.content.backdrops.length > 0;
		const hasTrailerClips = this.titleClips.length > 0;

		return hasCarouselImages || hasTrailerClips;
	}

	get backdropClasses() {
		return {
			backdrops__carousel: !this.shouldShowTrailerBackdrop && this.shouldShowCarouselBackdrop,
			'backdrops--trailer-playing': !!this.activeClipId,
		};
	}

	get shouldShowTrailerBackdrop(): boolean {
		// START AVOD
		if (this.isAVODActive) return !this.isPremium;
		// END AVOD

		return !this.isLoggedIn() && !!this.title.content.title && this.titleClips.length > 0;
	}

	get shouldShowCarouselBackdrop(): boolean {
		return this.imageUrls.length > 0;
	}

	get imageUrls(): String[] {
		const pathExtension = this.title.content.fullPath
			? `/${this.title.content.fullPath.substring(this.title.content.fullPath.lastIndexOf('/') + 1)}`
			: '';

		return this.title.content.backdrops.map(backdrop => backdrop.backdropUrl + pathExtension);
	}

	mounted() {
		if (this.activeClipId) {
			this.emitTrailerPlayingModel(null);
		}
		this.loadVideoAd();
	}

	@Watch('activeRoute')
	onRouteChange() {
		this.emitTrailerPlayingModel(null);
		this.loadVideoAd();
	}

	handleBackdropTrailer(activeClipId: string | null) {
		this.emitTrailerPlayingModel(activeClipId);
		this.emitBackdropClicked();
	}

	@Emit(ClipsEvent.MODEL)
	emitTrailerPlayingModel(activeClipId: string | null) {
		return activeClipId;
	}

	@Emit('backdrop-clicked')
	emitBackdropClicked() {}

	loadVideoAd() {
		if (!this.isLoggedIn()) VideoAdsHelper.loadVideoAd(this.webLocale);
	}
}
